import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, FolderOpen, Loader2, Pencil, Plus } from "lucide-react";
import { CreateKbSectionView } from "./CreateKbSectionView";
import { useCreateGameRuleTemplateWizard } from "../../hooks/useCreateGameRuleTemplateWizard";
import { openDraftInHost } from "../../services/hostDocumentService";
import { getSelectionTextPreview } from "../../services/gameRuleWordService";
import type {
  GameRuleFieldType,
  GameRuleWizardField,
} from "../../types/gameRuleTemplate";

const FIELD_TYPES: { value: GameRuleFieldType; label: string }[] = [
  { value: "text_input", label: "Text Input" },
  { value: "radio", label: "Radio Button" },
  { value: "select", label: "Dropdown" },
  { value: "date_picker", label: "Date Picker" },
  { value: "number_input", label: "Number Input" },
];

const fieldTypeBadgeLabel = (t: GameRuleFieldType): string =>
  FIELD_TYPES.find((x) => x.value === t)?.label ??
  (t === "checkbox" ? "Checkbox" : t === "text_area" ? "Text Area" : t);

export type CreateGameRuleTemplateViewProps = {
  readonly editTemplateId?: string;
};

type CreationUiStep = "upload" | "section" | "fields";

const stepOrder: readonly CreationUiStep[] = ["upload", "section", "fields"];

const stepCopy: Record<
  CreationUiStep,
  { readonly title: string; readonly subtitle: string; readonly step: string }
> = {
  upload: {
    title: "Generate Section Knowledgebase - Step 1 of 3",
    subtitle: "Upload & Open Document",
    step: "1",
  },
  section: {
    title: "Generate Section Knowledgebase - Step 2 of 3",
    subtitle: "Select Content & Define Section",
    step: "2",
  },
  fields: {
    title: "Generate Section Knowledgebase - Step 3 of 3",
    subtitle: "AI Field Extraction, Review & Save",
    step: "3",
  },
};

const TAG_OPTIONS = [
  "Eligibility",
  "Legal",
  "Prize",
  "Rules",
  "Dates",
  "Administration",
];

const createField = (
  label: string,
  type: GameRuleFieldType,
  required = true,
  description?: string,
): GameRuleWizardField & { description?: string } => {
  const key = label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return {
    id: crypto.randomUUID(),
    key: key || "FIELD",
    label,
    type,
    required,
    options: [],
    description,
  };
};

const isDocxFile = (file: File | null): boolean =>
  Boolean(file?.name.toLowerCase().endsWith(".docx"));

const fileToBase64 = async (file: File): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + chunkSize),
    );
  }
  return btoa(binary);
};

const EditGameRuleTemplateView = ({
  editTemplateId,
}: {
  readonly editTemplateId: string;
}) => {
  const navigate = useNavigate();
  const wizard = useCreateGameRuleTemplateWizard(editTemplateId);
  const [uiStep, setUiStep] = useState<CreationUiStep>(
    editTemplateId ? "fields" : "upload",
  );
  const [selectionPreview, setSelectionPreview] = useState("");
  const [sectionName, setSectionName] = useState("Eligibility: Who May Enter");
  const [selectedTag, setSelectedTag] = useState("Eligibility");
  const [sectionDescription, setSectionDescription] = useState(
    "Defines who is eligible to enter the sweepstakes",
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<string | null>(null);
  const [isOpeningDocument, setIsOpeningDocument] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (wizard.isEditMode && wizard.editLoadStatus === "ready") {
      setUiStep("fields");
    }
  }, [wizard.editLoadStatus, wizard.isEditMode]);

  useEffect(() => {
    if (uiStep !== "section") {
      return;
    }
    let disposed = false;
    const refresh = (): void => {
      void getSelectionTextPreview(220)
        .then((t) => {
          if (!disposed) {
            setSelectionPreview(t);
          }
        })
        .catch(() => {
          if (!disposed) {
            setSelectionPreview("");
          }
        });
    };
    const setup = async (): Promise<void> => {
      if (typeof Office === "undefined") {
        return;
      }
      await Office.onReady();
      if (disposed) {
        return;
      }
      refresh();
      if (Office.context?.document?.addHandlerAsync) {
        Office.context.document.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          refresh,
        );
      }
    };
    void setup();
    const iv = window.setInterval(refresh, 1500);
    return () => {
      disposed = true;
      window.clearInterval(iv);
      if (
        typeof Office !== "undefined" &&
        Office.context?.document?.removeHandlerAsync
      ) {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
        );
      }
    };
  }, [uiStep]);

  const activeIndex = stepOrder.indexOf(uiStep);
  const selectedContent = selectionPreview;
  const selectedContentLength = selectedContent
    .replace(/\s+/g, " ")
    .trim().length;
  const displayError = localError || wizard.error;
  const fieldsWithDescriptions = wizard.fields as Array<
    GameRuleWizardField & { description?: string }
  >;

  const progressBar = (
    <div className="-mx-4 -mt-4 border-b border-[var(--border)] bg-background px-3 pt-2">
      <div className="grid grid-cols-3 gap-1">
        {stepOrder.map((step, index) => (
          <div
            key={step}
            className={`h-1 rounded-full ${
              index < activeIndex
                ? "bg-emerald-500"
                : index === activeIndex
                  ? "bg-violet-700"
                  : "bg-muted"
            }`}
          />
        ))}
      </div>
      <div className="px-0 py-4">
        <h2 className="text-sm font-bold leading-tight text-primary">
          {stepCopy[uiStep].title}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {stepCopy[uiStep].subtitle}
        </p>
      </div>
    </div>
  );

  const goToSectionStep = async () => {
    setLocalStatus(null);
    if (!wizard.file || !isDocxFile(wizard.file)) {
      setLocalError("Please choose a .docx file before continuing.");
      return;
    }
    setLocalError(null);
    if (!wizard.name.trim()) {
      wizard.setName(wizard.file.name.replace(/\.docx$/i, ""));
    }
    setIsOpeningDocument(true);
    try {
      const base64 = await fileToBase64(wizard.file);
      await openDraftInHost(base64);
      setUiStep("section");
    } catch (err: unknown) {
      setLocalError(
        err instanceof Error
          ? err.message
          : "Unable to open the selected document in Word.",
      );
    } finally {
      setIsOpeningDocument(false);
    }
  };

  const addSection = () => {
    setLocalStatus(null);
    if (!sectionName.trim()) {
      setLocalError("Section name is required.");
      return;
    }
    if (!selectedTag.trim()) {
      setLocalError("Choose exactly one tag for this section.");
      return;
    }
    setLocalError(null);
    setUiStep("fields");
  };

  const saveToKnowledgebase = () => {
    setLocalStatus(null);
    setLocalError(null);
    void (async () => {
      if (wizard.templateId && wizard.name.trim()) {
        const ok = await wizard.persistDocumentAndFields();
        if (ok) {
          setLocalStatus("Saved to Knowledgebase.");
        }
        return;
      }
      setLocalStatus("Saved to Knowledgebase for this UI preview.");
    })();
  };

  const updateFieldLabel = (field: GameRuleWizardField, label: string) => {
    wizard.updateFieldLabel(field.id, label);
  };

  const addManualField = () => {
    wizard.replaceFields([
      ...wizard.fields,
      createField("New Field", "text_input", true, "Text - Required"),
    ]);
  };

  const footer = (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[var(--border)] bg-background px-3 py-2">
      {uiStep === "upload" ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="w-[78px] rounded border border-input bg-background px-3 py-2 text-xs font-medium text-foreground"
            onClick={() => navigate("/prizelogic")}
          >
            &larr; Cancel
          </button>
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md bg-violet-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            disabled={wizard.busy || isOpeningDocument}
            onClick={() => void goToSectionStep()}
          >
            {isOpeningDocument ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Opening...
              </span>
            ) : (
              "Continue \u2192"
            )}
          </button>
        </div>
      ) : uiStep === "section" ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="w-[68px] rounded border border-input bg-background px-3 py-2 text-xs font-medium text-foreground"
            onClick={() => {
              setLocalError(null);
              setUiStep("upload");
            }}
          >
            &larr; Back
          </button>
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md bg-violet-700 px-4 py-2 text-sm font-bold text-white"
            onClick={addSection}
          >
            Add to Section &rarr;
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            className="w-[68px] rounded border border-input bg-background px-3 py-2 text-xs font-medium text-foreground"
            onClick={() => {
              setLocalError(null);
              setUiStep("section");
            }}
          >
            &larr; Back
          </button>
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            disabled={wizard.busy}
            onClick={saveToKnowledgebase}
          >
            Save to Knowledgebase
          </button>
        </div>
      )}
    </div>
  );

  const uploadStep = (
    <section className="space-y-8 pt-6">
      {isOpeningDocument ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2
              className="h-9 w-9 animate-spin text-primary"
              aria-hidden
            />
            <p className="text-sm font-medium text-foreground">
              Opening document...
            </p>
            <p className="max-w-xs text-center text-xs text-muted-foreground">
              Loading the selected file into Word.
            </p>
          </div>
        </div>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          wizard.setFile(f);
          setLocalError(null);
        }}
      />
      <button
        type="button"
        className="mx-auto flex min-h-[170px] w-[88%] max-w-sm flex-col items-center justify-center rounded-md border-2 border-dashed border-violet-300 bg-violet-50/40 px-6 py-6 text-center"
        onClick={() => fileInputRef.current?.click()}
      >
        <FolderOpen
          className="h-9 w-9 text-amber-400"
          fill="currentColor"
          strokeWidth={1.4}
        />
        <p className="mt-3 text-xs text-foreground">
          Click to upload your document.
        </p>
        <span className="mt-3 rounded bg-primary px-5 py-2 text-xs font-bold text-primary-foreground">
          Browse Files...
        </span>
        {wizard.file ? (
          <span className="mt-3 max-w-full truncate text-xs font-semibold text-primary">
            {wizard.file.name}
          </span>
        ) : null}
      </button>
      <p className="text-xs leading-5 text-muted-foreground">
        Upload a source document. Selected content will be saved as individual
        sections in the Knowledgebase.
      </p>
    </section>
  );

  const sectionStep = (
    <section className="space-y-4 pt-3">
      <div className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        Content selected - {selectedContentLength} characters - ready for AI
        field extraction
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-foreground">
          Selected Content (for AI)
        </p>
        <div className="max-h-24 overflow-y-auto rounded border border-input bg-muted/40 px-3 py-2 text-xs leading-5 text-foreground shadow-inner">
          {selectedContent.split("\n").map((line) => (
            <p key={line || "blank"}>{line}</p>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="border-b border-violet-200 pb-1 text-[11px] font-bold uppercase tracking-wide text-violet-700">
          Section Details
        </div>
        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="game-rule-section-name"
        >
          Section Name *
          <input
            id="game-rule-section-name"
            className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            value={sectionName}
            onChange={(event) => setSectionName(event.target.value)}
          />
        </label>
        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="game-rule-section-tag"
        >
          Tag *
          <select
            id="game-rule-section-tag"
            className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            value={selectedTag}
            onChange={(event) => setSelectedTag(event.target.value)}
          >
            {TAG_OPTIONS.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
        </label>
        <p className="-mt-2 text-[11px] text-muted-foreground">
          Exactly one tag is allowed for each section.
        </p>
        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="game-rule-section-description"
        >
          Description
          <textarea
            id="game-rule-section-description"
            className="mt-1 min-h-[46px] w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            value={sectionDescription}
            onChange={(event) => setSectionDescription(event.target.value)}
          />
        </label>
      </div>
    </section>
  );

  const fieldsStep = (
    <section className="space-y-4 pt-3">
      <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
        {wizard.fields.length} fields defined - review, edit, or add below
      </div>

      <div className="space-y-2">
        <div className="border-b border-violet-200 pb-1 text-[11px] font-bold uppercase tracking-wide text-violet-700">
          Input Fields
        </div>
        <div className="overflow-hidden rounded border border-[var(--border)] bg-card">
          {fieldsWithDescriptions.map((field) => (
            <div
              key={field.id}
              className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2 last:border-b-0"
            >
              <FileText
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                {editingFieldId === field.id ? (
                  <input
                    className="w-full rounded border border-input bg-background px-2 py-1 text-xs font-semibold"
                    value={field.label}
                    onChange={(event) =>
                      updateFieldLabel(field, event.target.value)
                    }
                    onBlur={() => setEditingFieldId(null)}
                    autoFocus
                  />
                ) : (
                  <p className="truncate text-xs font-bold text-primary">
                    {field.label}
                    {field.required ? " *" : ""}
                  </p>
                )}
                <p className="truncate text-[11px] text-muted-foreground">
                  {field.description ?? fieldTypeBadgeLabel(field.type)}
                </p>
                <p className="truncate text-[11px] text-emerald-700">
                  [{field.key}] in document
                </p>
              </div>
              <button
                type="button"
                className="rounded bg-muted px-2 py-1 text-foreground hover:bg-accent"
                aria-label={`Edit ${field.label}`}
                onClick={() => setEditingFieldId(field.id)}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </button>
              <button
                type="button"
                className="rounded bg-muted px-2 py-1 text-destructive hover:bg-destructive/10"
                aria-label={`Remove ${field.label}`}
                onClick={() => wizard.removeField(field.id)}
              >
                X
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-violet-300 bg-violet-50/40 px-3 py-2 text-xs font-semibold text-violet-700"
          onClick={addManualField}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add Field Manually
        </button>
      </div>

      <p className="text-[11px] leading-5 text-muted-foreground">
        Save stores this section to the Knowledgebase immediately. Then add
        another section or finish.
      </p>
    </section>
  );

  return (
    <div className="min-h-[calc(100vh-96px)] pb-16">
      {progressBar}
      {wizard.isEditMode && wizard.editLoadStatus === "loading" ? (
        <div className="rounded-lg border border-[var(--border)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
          Loading template…
        </div>
      ) : null}

      {displayError &&
      !(wizard.isEditMode && wizard.editLoadStatus === "loading") ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {displayError}
        </div>
      ) : null}

      {localStatus ? (
        <div className="mt-3 rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {localStatus}
        </div>
      ) : null}

      {uiStep === "upload"
        ? uploadStep
        : uiStep === "section"
          ? sectionStep
          : fieldsStep}
      {footer}
    </div>
  );
};

export const CreateGameRuleTemplateView = ({
  editTemplateId,
}: CreateGameRuleTemplateViewProps = {}) => {
  if (!editTemplateId) {
    return <CreateKbSectionView />;
  }
  return <EditGameRuleTemplateView editTemplateId={editTemplateId} />;
};
